// ═══════════════════════════════════════════════════════════════════════════
// AI Command Schemas — LAYER-MANAGEMENT domain (T7, agent-bridge)
// ═══════════════════════════════════════════════════════════════════════════
//
// Zod schemas for the LAYER-TABLE-management actions the AI safe contour may
// emit: CREATE_LAYER (author a new layer row), RENAME_LAYER (rename an
// existing row, cascading to every entity still on it), and SET_ACTIVE_LAYER
// (move the CURRENT-layer pointer, CLAYER, for entities the agent creates
// NEXT). As with every other AI-command domain, each schema here is a PLAIN
// DATA CONTRACT — it does NOT mutate anything. Translation to internal
// `EditorCommand`s happens in `layerDispatcher.ts` / `layerTranslators.ts`.
//
// ── SCOPE — mirrors `AI-BRIDGE-EDIT-TASKS.md` T7 EXACTLY (3 actions) ────────
//
// T7's own scope line lists FOUR items: "CREATE_LAYER/SET_ACTIVE_LAYER/
// RENAME/назначение слоя" ("assign layer [to an entity]"). The FOURTH —
// reassigning an EXISTING entity's `layer` property — is the ТЗ §10.1
// CHPROP/PROPERTIES channel (`aiCallable:false`, "reserved for user/UI";
// system.ts CHPROP/PROPERTIES comments). ТЗ is the source-of-truth per
// CLAUDE.md, and T5 (property/text/dimstyle edit, including a `layer` field
// on `SET_PROPERTIES`) is EXPLICITLY gated pending a ТЗ §10.1 amendment
// decision (docs/MASTERPLAN.md §Р3 T5 row: "🚧 BLOCKED ON ТЗ §10.1 DECISION").
// "Assign an existing entity to a different layer" is exactly that gated
// action wearing a LAYER-flavoured name — it is therefore DELIBERATELY
// EXCLUDED here, mirroring the T5 gate rather than re-deriving a separate
// exception. See docs/MASTERPLAN.md §Р3 T7 row's "(может пересекать §10.1
// consideration)" flag — this is the resolution of that flag.
//
// The remaining THREE actions are genuinely OUTSIDE the T5/CHPROP gate: none
// of them reassigns any EXISTING entity's `layer` field.
//   • CREATE_LAYER   — authors a NEW row in the layer TABLE. No entity is
//     touched. AutoCAD's LAYER "New", a distinct command from CHPROP.
//   • RENAME_LAYER    — renames an EXISTING layer TABLE row; every entity
//     that was "on" the old name stays "on" the SAME (renamed) layer — a
//     structural consequence of the TABLE rename, not a discretionary
//     per-entity reassignment (mirrors how `header/rename-dimension-style`'s
//     cascade is not gated by the T5 property-edit restriction either).
//     AutoCAD's LAYER "Rename".
//   • SET_ACTIVE_LAYER — moves the CURRENT-layer pointer (CLAYER), which only
//     affects entities the agent creates AFTER this call — it never touches
//     an existing entity. Mirrors the ALREADY-shipped, non-gated SET_
//     CANNOSCALE / DIMSTYLE "Set Current" precedent (both are current-pointer
//     changes routed through the SAME `properties/apply`-with-empty-selection
//     mechanic used here).
// ═══════════════════════════════════════════════════════════════════════════

import { z } from "zod";

// ─── CREATE_LAYER → header/add-layer (LAYER New) ────────────────────────────
//
// `name` — the new layer's name. `.trim()` runs BEFORE `.min(1)` (local
// Codex round-1 P2) so a whitespace-only proposal (`"   "`, length ≥ 1) is
// REJECTED at the schema boundary instead of slipping through
// `z.string().min(1)` (which only checks raw length, not trimmed content) —
// without it `translateCreateLayer` would build a valid-looking
// `header/add-layer` for a blank-looking layer name, bypassing the
// human-facing `addLayer` wrapper's trim+empty guard entirely (the AI safe
// contour dispatches EditorCommands directly, never through that wrapper).
// A value with real content surrounded by whitespace (`" Walls "`) is
// normalised to `"Walls"`, matching the wrapper's own `.trim()` behaviour.
// Duplicate names are still rejected by the reducer (idempotent no-op — see
// styleTablesReducer.ts's `appendStyleTableEntry`).
//
// Round-25 P2 (github PR #527 review) — `name !== "0"` is ALSO rejected:
// layer "0" is mandatory (AutoCAD parity — always present, undeletable), so
// `CREATE_LAYER{name:"0"}` always collides with the ALREADY-existing "0" row
// and the reducer's duplicate guard silently no-ops it — the SAME
// false-positive "success reported to the agent" shape `RENAME_LAYER`'s
// `newName !== "0"` refine (round-21) closed for the rename side.
export const createLayerAiSchema = z
  .object({
    action: z.literal("CREATE_LAYER"),
    name: z.string().trim().min(1),
  })
  .refine((v) => v.name !== "0", {
    message: 'Layer "0" already exists (AutoCAD parity — cannot CREATE_LAYER "0").',
    path: ["name"],
  });
export type CreateLayerAiCommand = z.infer<typeof createLayerAiSchema>;

// ─── RENAME_LAYER → header/rename-layer (LAYER Rename) ──────────────────────
//
// Mirrors `renameBlockAiSchema` / `header/rename-dimension-style` shape
// exactly (oldName/newName pair), `.trim().min(1)` per the CREATE_LAYER note
// above (local Codex round-1 P2 — a whitespace-only `newName` must be
// rejected at the boundary, not silently rename the layer + every entity on
// it to an empty string). Layer "0" cannot be renamed (AutoCAD parity —
// mirrors "Standard" for DIMSTYLE); the reducer no-ops rather than throwing
// on an invalid oldName/newName (fail-closed, mirrors every sibling
// style-table command) — see styleTablesReducer.ts's `header/rename-layer`
// case, which carries its OWN `!trimmedNew` guard as the defense-in-depth
// backstop for any FUTURE caller that bypasses this schema.
//
// Local Codex round-13 P2 — `oldName === "0"` is STATICALLY invalid (layer
// "0" can never be renamed, in ANY document state), yet the field schema
// alone accepted it (`.trim().min(1)` only rejects blank/whitespace). Without
// the `.refine` below, an agent proposing `RENAME_LAYER {oldName:"0", ...}`
// passed schema validation, `translateRenameLayer` built a well-formed
// `header/rename-layer` command, and the reducer's OWN `trimmedOld === "0"`
// guard silently no-opped — the safe contour reported the dispatch as
// SUCCESSFUL despite renaming nothing, misleading the agent into believing
// its request was honoured. Rejecting it here (a statically-decidable
// input shape, not a document-state check like duplicate-name) surfaces the
// error at the boundary instead of masking it as a false-positive success.
// Local Codex round-21 P2 (github PR #527 review) — the `oldName !== "0"`
// refine above only rejected renaming layer 0 AWAY — it did not reject
// renaming some OTHER layer TO "0". Layer "0" is mandatory (AutoCAD parity —
// always present, undeletable), so `RENAME_LAYER {newName:"0"}` is ALSO
// statically invalid: the reducer's `newNameTaken` collision guard would
// always find "0" already occupied and no-op — the SAME "false-positive
// success reported to the agent" shape round-13's `oldName` fix closed.
// Rejecting it here (a second, independent `.refine`) surfaces the error at
// the boundary instead of masking it as a silent no-op.
export const renameLayerAiSchema = z
  .object({
    action: z.literal("RENAME_LAYER"),
    oldName: z.string().trim().min(1),
    newName: z.string().trim().min(1),
  })
  .refine((v) => v.oldName !== "0", {
    message: 'Layer "0" cannot be renamed (AutoCAD parity).',
    path: ["oldName"],
  })
  .refine((v) => v.newName !== "0", {
    message: 'Layer "0" is mandatory — cannot rename another layer to "0" (AutoCAD parity).',
    path: ["newName"],
  });
export type RenameLayerAiCommand = z.infer<typeof renameLayerAiSchema>;

// ─── SET_ACTIVE_LAYER → properties/apply (empty selection) — CLAYER ────────
//
// `name` — the layer to make current. `.trim().min(1)` per the CREATE_LAYER
// note above. Translates to the SAME "no-selection properties/apply"
// mechanic `setCurrentLayer` (editorStoreStyleTables.ts) and
// `setCurrentDimensionStyle` use — see `layerTranslators.ts` for the full
// rationale (including why this translator is intentionally thin/total,
// unlike the human-facing store wrapper, which validates existence).
export const setActiveLayerAiSchema = z.object({
  action: z.literal("SET_ACTIVE_LAYER"),
  name: z.string().trim().min(1),
});
export type SetActiveLayerAiCommand = z.infer<typeof setActiveLayerAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Discriminated union + domain routing
// ═══════════════════════════════════════════════════════════════════════════

export const layerAiCommandSchema = z.discriminatedUnion("action", [
  createLayerAiSchema,
  renameLayerAiSchema,
  setActiveLayerAiSchema,
]);
export type LayerAiCommand = z.infer<typeof layerAiCommandSchema>;

/** A batch of LAYER-domain commands the AI can emit in one response. */
export const layerAiCommandBatchSchema = z.array(layerAiCommandSchema);
export type LayerAiCommandBatch = z.infer<typeof layerAiCommandBatchSchema>;

/** Convenience: literal action names for the router, guard map, telemetry. */
export const LAYER_AI_ACTIONS = [
  "CREATE_LAYER",
  "RENAME_LAYER",
  "SET_ACTIVE_LAYER",
] as const;
export type LayerAiAction = (typeof LAYER_AI_ACTIONS)[number];

const LAYER_ACTION_SET: ReadonlySet<string> = new Set(LAYER_AI_ACTIONS);

/**
 * Router predicate: does this AI command belong to the LAYER-management
 * domain? Disjoint from every other domain's action set (block / entity /
 * draw-modify / view-space / matchprop / base-prim), so `aiDispatcher.ts`
 * can narrow the broad `AiCommand` union with a plain membership test.
 */
export function isLayerAiCommand(command: {
  action: string;
}): command is LayerAiCommand {
  return LAYER_ACTION_SET.has(command.action);
}
